import React from "react";
import { Icon } from "./icons";
import ModeTabs from "./ModeTabs";

/**
 * Hero of «Поиск» (iTruck_Search_Unified): title, subtitle, full-width mode tabs and four metric tiles
 * (icon chip, big number, label, trend chip + 6 mini bars).
 * The trend chip appears ONLY when `series` (see dailySeries) is a real time series; the bars are decoration.
 */
export const HERO = {
  transport: { title: "Найдите транспорт", text: "Тысячи перевозчиков. Актуальные машины. Прямые контакты." },
  loads: { title: "Найдите груз", text: "Актуальные грузы. Удобные маршруты. Прямые контакты." },
};

const DAY = 86400000;
const startOfDay = (t) => {
  const d = new Date(t);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/**
 * Count items per day over the last `days` days and compare with the `days` before.
 * Returns { bars: number[days] (oldest → newest), cur, prev, pct|null } or null when the current window is empty.
 */
export function dailySeries(items, getDate, days = 7) {
  if (!items || !items.length) return null;
  const today = startOfDay(Date.now());
  const cur = Array(days).fill(0);
  let prev = 0;
  for (const it of items) {
    const raw = getDate(it);
    if (!raw) continue;
    const t = new Date(raw).getTime();
    if (Number.isNaN(t)) continue;
    const idx = Math.round((today - startOfDay(t)) / DAY); // 0 = today
    if (idx >= 0 && idx < days) cur[days - 1 - idx] += 1;
    else if (idx >= days && idx < days * 2) prev += 1;
  }
  const total = cur.reduce((a, b) => a + b, 0);
  if (!total) return null;
  return { bars: cur, cur: total, prev, pct: prev > 0 ? Math.round(((total - prev) / prev) * 100) : null };
}

/** Mini bars of every metric tile: decoration only, the same silhouette as the design. */
export const DECOR_BARS = [10, 15, 20, 15, 23, 29];

/**
 * Trend chip of a real series: «↑ +12%» against the 7 days before; when there was nothing before,
 * the number of new items instead («+5 за 7 дн.»). null when there is nothing to show.
 */
export function trendOf(series) {
  if (!series) return null;
  if (series.pct != null) return { down: series.pct < 0, text: `${series.pct < 0 ? "↓ −" : "↑ +"}${Math.abs(series.pct)}%` };
  return series.cur > 0
    ? {
        down: false,
        text: (
          <>
            ↑ +{series.cur}
            <span className="chip-tail"> за 7 дн.</span>
          </>
        ),
      }
    : null;
}

export function TileBars() {
  return (
    <span className="bars decor" aria-hidden="true">
      {DECOR_BARS.map((h, i) => (
        <i key={i} style={{ height: h }} />
      ))}
    </span>
  );
}

export function MetricTile({ d, value, label, series, seriesHint, tone = 0 }) {
  const trend = trendOf(series);
  return (
    <article className={`metric tone-${tone}`}>
      <span className="metric-icon">
        <Icon d={d} />
      </span>
      <b>{value}</b>
      <small>{label}</small>
      {trend && (
        <em className={trend.down ? "down" : ""} title={seriesHint}>
          {trend.text}
        </em>
      )}
      <TileBars />
    </article>
  );
}

export default function SearchHero({ mode, tiles }) {
  const shown = tiles.filter(Boolean);
  return (
    <section className="search-hero">
      <div className="intro">
        {/* «Поиск» is the same for both modes (iTruck_Search_Unified); HERO is «Главная»'s */}
        <h1>Поиск</h1>
        <p>Ищите грузы и транспорт по всей стране и за её пределами. Прямые контакты перевозчиков и грузоотправителей.</p>
        <ModeTabs mode={mode} />
      </div>
      <div className="search-metrics" style={{ "--n": Math.max(1, shown.length) }}>
        {shown.map((t, i) => (
          <MetricTile key={i} {...t} />
        ))}
      </div>
    </section>
  );
}
